import React, { useEffect, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { LogoMark } from '../components/Logo'
import Icon from '../components/Icons'
import { navLinks, company } from '../data/site'

const Nav = () => {
  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => setMenuOpen(false), [pathname])

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [menuOpen])

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setMenuOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Every page opens on a near-black band, so the bar starts inverted.
  const solid = scrolled && !menuOpen

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ease-out-expo ${
          solid
            ? 'border-b border-ink-950/10 bg-paper-100/80 py-3 backdrop-blur-xl'
            : 'border-b border-transparent py-6'
        }`}
      >
        <div className="container flex items-center justify-between gap-8">
          <Link
            to="/"
            className="flex items-center gap-3"
            aria-label={`${company.name} — home`}
          >
            <LogoMark
              className={`h-10 w-10 shrink-0 transition-colors duration-500 ${
                solid ? 'text-ink-950' : 'text-paper-100'
              }`}
            />
            <span className="flex flex-col leading-none">
              <span
                className={`font-display text-base font-semibold tracking-[0.14em] transition-colors duration-500 ${
                  solid ? 'text-ink-950' : 'text-paper-100'
                }`}
              >
                ALDARON LIMITED
              </span>
              <span
                className={`mt-1.5 font-display text-[0.55rem] font-semibold uppercase tracking-[0.16em] transition-colors duration-500 ${
                  solid ? 'text-accent-600' : 'text-accent-400'
                }`}
              >
                Innovate. Empower. Deliver.
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {navLinks.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `group flex items-baseline gap-2 font-sans text-sm font-medium transition-colors duration-300 ${
                    solid
                      ? isActive
                        ? 'text-ink-950'
                        : 'text-ink-500 hover:text-ink-950'
                      : isActive
                      ? 'text-paper-100'
                      : 'text-paper-100/55 hover:text-paper-100'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`text-[0.6rem] tabular-nums transition-opacity ${
                        isActive ? 'opacity-100' : 'opacity-40'
                      } ${solid ? 'text-accent-500' : 'text-accent-400'}`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="relative">
                      {link.label}
                      <span
                        className={`absolute -bottom-1 left-0 h-px w-full origin-left transition-transform duration-500 ease-out-expo ${
                          solid ? 'bg-ink-950' : 'bg-paper-100'
                        } ${isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'}`}
                      />
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              className={`hidden btn px-6 py-3 lg:inline-flex ${
                solid ? 'btn-outline' : 'btn-ghost-dark'
              }`}
            >
              Get IT support
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className={`relative z-50 inline-flex h-11 w-11 items-center justify-center rounded-full ring-1 ring-inset transition-colors duration-500 lg:hidden ${
                menuOpen || !solid
                  ? 'text-paper-100 ring-paper-100/25'
                  : 'text-ink-950 ring-ink-950/15'
              }`}
            >
              <span className="relative block h-3.5 w-5">
                <span
                  className={`absolute left-0 block h-px w-5 bg-current transition-all duration-500 ease-out-expo ${
                    menuOpen ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0'
                  }`}
                />
                <span
                  className={`absolute left-0 block h-px w-5 bg-current transition-all duration-500 ease-out-expo ${
                    menuOpen ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0'
                  }`}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen mobile overlay */}
      <div
        id="mobile-menu"
        className={`fixed inset-0 z-40 bg-ink-950 grain transition-[opacity,visibility] duration-500 ease-out-expo lg:hidden ${
          menuOpen ? 'visible opacity-100' : 'invisible opacity-0'
        }`}
      >
        <div className="container flex h-full flex-col justify-between pb-12 pt-32">
          <nav className="flex flex-col" aria-label="Mobile">
            {navLinks.map((link, i) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `flex items-baseline gap-5 border-b border-paper-100/10 py-6 font-display text-4xl font-medium tracking-tightest transition-colors ${
                    isActive ? 'text-paper-100' : 'text-paper-100/45'
                  }`
                }
                style={{
                  transitionDelay: menuOpen ? `${120 + i * 60}ms` : '0ms',
                  transform: menuOpen ? 'none' : 'translateY(14px)',
                  opacity: menuOpen ? 1 : 0,
                  transitionProperty: 'transform, opacity, color',
                  transitionDuration: '600ms',
                  transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                <span className="text-xs tabular-nums text-accent-400">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="space-y-6">
            <Link to="/contact" className="btn btn-light w-full">
              Get IT support
              <Icon name="arrow" className="h-4 w-4" />
            </Link>
            <div className="flex flex-col gap-2">
              <a
                href={`tel:${company.phones[0].replace(/\s/g, '')}`}
                className="text-sm text-paper-100/55"
              >
                {company.phones[0]}
              </a>
              <a
                href={`mailto:${company.emails[0]}`}
                className="text-sm text-paper-100/55"
              >
                {company.emails[0]}
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Nav
